/**
 * Google Sheets API 服務
 *
 * 設定步驟：
 * 1. 前往 Google Cloud Console (console.cloud.google.com)
 * 2. 建立新專案或選擇現有專案
 * 3. 啟用 Google Sheets API
 * 4. 建立 API 金鑰（憑證 > 建立憑證 > API 金鑰）
 * 5. 在專案根目錄建立 .env 檔案，加入：
 *    REACT_APP_GOOGLE_SHEETS_API_KEY=你的API金鑰
 *    REACT_APP_GOOGLE_SHEETS_ID=你的試算表ID
 * 6. 將你的 Google Sheet 設定為「知道連結的任何人」可檢視
 *
 * Google Sheet 格式：
 * 第一列（標題列）：日期 | 內容
 * 第二列開始：實際資料
 *
 * 範例：
 * | 日期       | 內容                    |
 * |-----------|------------------------|
 * | 2025/11/18| 台北馬拉松加油～🙌🙌🙌   |
 * | 2025/11/15| 準備開始訓練！           |
 */

const SHEETS_API_BASE_URL = "https://sheets.googleapis.com/v4/spreadsheets";
const GROUP_REGISTRATION_SHEET_ID = "19vOXieEB_AKW8zb9prg8mo2QCZ9RgaawdAfoZd4vJQs";

/**
 * 從 Google Sheets 取得最新消息
 * @returns {Promise<{date: string, content: string}>} 最新的一筆消息
 */
export const fetchLatestNews = async () => {
    try {
        const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
        const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEETS_ID;

        if (!API_KEY || !SHEET_ID) {
            console.warn("Google Sheets API credentials not found. Using fallback data.");
            return {
                date: "2025/11/18",
                content: "台北馬拉松加油～🙌🙌🙌",
            };
        }

        // 建立 API URL
        const url = `${SHEETS_API_BASE_URL}/${SHEET_ID}/values/A2:D?key=${API_KEY}`;

        console.log("Fetching from Google Sheets...");

        const response = await fetch(url);

        // 詳細的錯誤處理
        if (!response.ok) {
            const errorText = await response.text();
            console.error("Google Sheets API Error Response:", errorText);

            try {
                const errorData = JSON.parse(errorText);
                console.error("Error Details:", errorData);

                // 提供更友善的錯誤訊息
                if (response.status === 400) {
                    console.error("可能的原因：");
                    console.error("1. Sheet ID 不正確");
                    console.error("2. 試算表沒有設定為公開");
                    console.error("3. 工作表名稱不是預設的（如果不是 Sheet1，請修改 RANGE）");
                } else if (response.status === 403) {
                    console.error("可能的原因：");
                    console.error("1. API 金鑰無效");
                    console.error("2. Google Sheets API 未啟用");
                    console.error("3. API 金鑰的使用權限不足");
                }
            } catch (e) {
                console.error("Raw error response:", errorText);
            }

            throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Google Sheets Response:", data);

        // 檢查是否有資料
        if (!data.values || data.values.length === 0) {
            console.warn("No data found in Google Sheets");
            return {
                date: "2025/11/18",
                content: "台北馬拉松加油～🙌🙌🙌",
            };
        }

        // 取得最新的一筆（第一筆）資料
        const latestRow = data.values[0];

        return {
            date: latestRow[0] || "日期未設定",
            content: latestRow[1] || "內容未設定",
        };
    } catch (error) {
        console.error("Error fetching news from Google Sheets:", error);
        // 返回預設資料
        return {
            date: "2025/11/18",
            content: "台北馬拉松加油～🙌🙌🙌",
        };
    }
};

/**
 * 解析日期字串為 Date 物件
 * 支援格式：2025/11/18、2025-11-18
 */
const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);

    // 移除空白
    const cleaned = dateStr.trim();

    // 嘗試解析不同格式
    const formats = [
        // 2025/11/18
        /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
        // 2025-11-18
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
    ];

    for (const format of formats) {
        const match = cleaned.match(format);
        if (match) {
            const [, year, month, day] = match;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
    }

    // 如果都不匹配，嘗試直接解析
    const date = new Date(cleaned);
    return isNaN(date.getTime()) ? new Date(0) : date;
};

/**
 * 從 Google Sheets 取得所有消息（依日期排序，最新在前）
 * @returns {Promise<Array<{id: string, date: string, content: string}>>} 所有消息列表
 */
export const fetchAllNews = async () => {
    try {
        const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
        const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEETS_ID;

        if (!API_KEY || !SHEET_ID) {
            console.warn("Google Sheets API credentials not found. Using fallback data.");
            return [
                {
                    id: "1",
                    date: "2025/11/18",
                    content: "台北馬拉松加油～🙌🙌🙌",
                },
            ];
        }

        const url = `${SHEETS_API_BASE_URL}/${SHEET_ID}/values/A2:E?key=${API_KEY}`;

        const response = await fetch(url);

        if (!response.ok) {
            const errorText = await response.text();
            console.error("Google Sheets API Error Response:", errorText);
            throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.values || data.values.length === 0) {
            console.warn("No data found in Google Sheets");
            return [
                {
                    id: "1",
                    date: "2025/11/18",
                    content: "台北馬拉松加油～🙌🙌🙌",
                },
            ];
        }

        // 過濾並轉換資料
        const newsItems = data.values
            .filter((row) => {
                // 確保有日期、標題和內容才顯示
                const hasDate = row[0] && row[0].trim();
                const hasTitle = row[1] && row[1].trim();
                const hasContent = row[2] && row[2].trim();
                return hasDate && hasTitle && hasContent;
            })
            .map((row, index) => ({
                id: `news-${index}`,
                date: row[0],
                title: row[1],
                content: row[2],
                category: row[3] || "公告",
                isArchived: String(row[4] || "").toUpperCase() === "TRUE", // 是否下架
                dateObj: parseDate(row[0]), // 用於排序
            }));

        // 依日期排序（最新在前）
        // newsItems.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

        // 移除暫時用的 dateObj
        return newsItems.map(({ id, date, title, content, category, isArchived }) => ({
            id,
            date,
            title,
            content,
            category,
            isArchived,
        }));
    } catch (error) {
        console.error("Error fetching all news from Google Sheets:", error);
        return [
            {
                id: "1",
                date: "2025/11/18",
                title: "台北馬拉松加油",
                content: "台北馬拉松加油～🙌🙌🙌",
                category: "賽事",
                isArchived: false,
            },
        ];
    }
};

/**
 * 從 Google Sheets 取得團購報名資料
 * Sheet: 喵喵團購發佈
 * Columns: 日期(A), 種類(B), 賽事名稱(C), 收單日期(D), 連結(E), 備註(F)
 * @returns {Promise<Array>} 團購列表
 */
export const fetchGroupRegistrations = async () => {
    try {
        const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
        // 使用使用者指定的 Sheet ID
        const SHEET_ID = GROUP_REGISTRATION_SHEET_ID;

        if (!API_KEY) {
            console.warn("Google Sheets API key not found.");
            return [];
        }

        // 指定工作表名稱和範圍
        const range = "喵喵團購發佈!A2:G";
        const url = `${SHEETS_API_BASE_URL}/${SHEET_ID}/values/${encodeURIComponent(
            range
        )}?key=${API_KEY}`;

        const response = await fetch(url);

        if (!response.ok) {
            const errorText = await response.text();
            console.error("Google Sheets API Error Response:", errorText);
            throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.values || data.values.length === 0) {
            console.warn("No data found in Google Sheets");
            return [];
        }

        // 轉換資料格式
        const registrations = data.values
            .filter((row) => {
                // 確保有種類、賽事名稱和連結才顯示
                const hasType = row[1] && row[1].trim();
                const hasName = row[2] && row[2].trim();
                const hasUrl = row[4] && row[4].trim();
                return hasType && hasName && hasUrl;
            })
            .map((row, index) => ({
                id: `registration-${index}`,
                eventDate: row[0] || "", // A: 日期
                type: row[1] || "", // B: 種類
                eventName: row[2] || "", // C: 賽事名稱
                deadline: row[3] || "", // D: 收單日期
                url: row[4] || "", // E: 連結
                eventSubtitle: row[5] || "", // F: 備註
                isArchived: String(row[6] || "").toUpperCase() === "TRUE", // G: 是否下架
                dateObj: parseDate(row[0]), // 用於排序
            }));

        // 依日期排序（最新在前）
        // registrations.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

        return registrations;
    } catch (error) {
        console.error("Error fetching group registrations:", error);
        return [];
    }
};

/**
 * 取得使用者裝置類型
 * @returns {string} 'mobile' | 'tablet' | 'desktop'
 */
const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return "tablet";
    }
    if (
        /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
            ua
        )
    ) {
        return "mobile";
    }
    return "desktop";
};

/**
 * 記錄按鈕點擊事件到 Google Sheets
 * @param {string} buttonName - 按鈕名稱
 * @param {string} buttonPath - 按鈕路徑/連結
 * @returns {Promise<boolean>} 是否成功記錄
 */
export const trackButtonClick = async (buttonName, buttonPath) => {
    try {
        const APPS_SCRIPT_URL = process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL;

        if (!APPS_SCRIPT_URL) {
            console.warn("Google Apps Script URL not found. Click tracking disabled.");
            return false;
        }

        const now = new Date();
        const date = now.toLocaleDateString("zh-TW", { timeZone: "Asia/Taipei" });
        const time = now.toLocaleTimeString("zh-TW", { timeZone: "Asia/Taipei" });
        const deviceType = getDeviceType();

        const response = await fetch(APPS_SCRIPT_URL, {
            method: "POST",
            mode: "no-cors", // 必須使用 no-cors 模式
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                date,
                time,
                buttonName,
                buttonPath,
                deviceType,
            }),
        });

        // no-cors 模式下無法讀取回應，但如果沒有拋出錯誤就表示請求已發送
        console.log("Click tracked:", { date, time, buttonName, buttonPath, deviceType });
        return true;
    } catch (error) {
        console.error("Error tracking button click:", error);
        return false;
    }
};

/**
 * 從 Google Sheets 取得計算機任意門按鈕資料
 * Sheet: 計算機任意門
 * Columns: 按鈕名稱(A), 連結(B), 圖示名稱(C), 排序(D), 是否啟用(E)
 * @returns {Promise<Array>} 按鈕列表
 */
export const fetchCalculatorButtons = async () => {
    try {
        const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
        const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEETS_ID;

        // 預設資料 (當 API 未設定或失敗時使用)
        const fallbackData = [
            {
                id: "default-1",
                title: "一般路跑",
                link: "/Basic?tab=ROAD",
                icon: "Footprints",
                order: 1,
            },
            {
                id: "default-2",
                title: "操場跑道",
                link: "/Basic?tab=TRACK",
                icon: "Activity",
                order: 2,
            },
            {
                id: "default-3",
                title: "2025 台北馬拉松",
                link: "/TaipeiMarathon",
                icon: "Trophy",
                order: 3,
            },
        ];

        if (!API_KEY || !SHEET_ID) {
            console.warn(
                "Google Sheets API credentials not found. Using fallback data for calculator buttons."
            );
            return fallbackData;
        }

        const range = "首頁-計算機任意門!A2:E";
        const url = `${SHEETS_API_BASE_URL}/${SHEET_ID}/values/${encodeURIComponent(
            range
        )}?key=${API_KEY}`;

        const response = await fetch(url);

        if (!response.ok) {
            console.warn("Calculator buttons sheet not found or error, using fallback.");
            return fallbackData;
        }

        const data = await response.json();

        if (!data.values || data.values.length === 0) {
            return fallbackData;
        }

        const buttons = data.values
            .filter((row) => {
                // 檢查是否啟用 (E欄)，預設為 TRUE
                const isActive = !row[4] || String(row[4]).trim().toUpperCase() === "TRUE";
                // 必須有標題和連結
                return row[0] && row[1] && isActive;
            })
            .map((row, index) => ({
                id: `calc-btn-${index}`,
                title: row[0], // A: 按鈕名稱
                link: row[1], // B: 連結
                icon: row[2] || "Footprints", // C: 圖示名稱
                order: parseInt(row[3] || "999", 10), // D: 排序
            }))
            .sort((a, b) => a.order - b.order);

        return buttons.length > 0 ? buttons : fallbackData;
    } catch (error) {
        console.error("Error fetching calculator buttons:", error);
        return [
            {
                id: "default-1",
                title: "一般路跑",
                link: "/Basic?tab=ROAD",
                icon: "Footprints",
                order: 1,
            },
            {
                id: "default-2",
                title: "操場跑道",
                link: "/Basic?tab=TRACK",
                icon: "Activity",
                order: 2,
            },
            {
                id: "default-3",
                title: "2025 台北馬拉松",
                link: "/TaipeiMarathon",
                icon: "Trophy",
                order: 3,
            },
        ];
    }
};
